import { useNavigate } from "react-router";
import type { PlayerProfile } from "@wr/contracts";
import { t } from "@wr/i18n";
import {
  Avatar,
  Button,
  ChevronIcon,
  ChromeNickname,
  CoinPlate,
  ProfileFlyout,
  RoleChip,
} from "@wr/ui";
import { useOverlay } from "../app/OverlayProvider";
import { useSeat } from "../app/SeatProvider";

export interface ProfileFlyoutContentProps {
  profile: PlayerProfile;
  /**
   * **The same number the top-bar chip is rendering** — `useBalance()`'s value,
   * passed down rather than re-derived (T-217). `null` is Ivy §9.2's `—`: no
   * source, which is a different claim from `0`.
   */
  balanceCoins: number | null;
  onLogout: () => void;
  /** Rows that need the network are dimmed and labelled while it is down. */
  offline: boolean;
}

/**
 * The profile flyout (design spec §12).
 *
 * Two **LOCKED** deviations from PRD §5.3's row list, both for the same reason:
 *
 * 1. **No stats grid in S2.** The import's GAMES / WIN RATE / STREAK trio has
 *    no data behind it until S6-1, and a row of zeroes implies a game that was
 *    played and lost. The block is omitted entirely, not zeroed.
 * 2. **Guests do not see disabled rows.** "Change nickname" and "Change avatar"
 *    are *replaced* by one conversion card. A greyed row teaches a guest that
 *    the app is broken for them; a card teaches them what to do next.
 *
 * The coin plate renders the `balanceCoins` prop — the same value the top bar's
 * chip is showing, because two numbers about one wallet on one screen is a
 * defect waiting for a moment to be seen (T-217).
 */
export function ProfileFlyoutContent({
  profile,
  balanceCoins,
  onLogout,
  offline,
}: ProfileFlyoutContentProps) {
  const { overlay, close, openConversion } = useOverlay();
  const { seatHeld } = useSeat();
  const navigate = useNavigate();

  function go(to: string) {
    close();
    void navigate(to);
  }

  return (
    <ProfileFlyout open={overlay === "flyout"} onClose={close} label={t("flyout.open")}>
      <div className="wr-row" style={{ gap: 12, marginBottom: 16 }}>
        <Avatar
          playerId={profile.id}
          nickname={profile.nickname}
          url={profile.avatarUrl}
          size={54}
        />
        {/* `min-width: 0` on the flex parent too — §14.1 ruling B item 2. */}
        <span className="wr-stack" style={{ gap: 4, minWidth: 0, alignItems: "flex-start" }}>
          {/*
            **`nameExposure="self"` — this span IS the surface's exposure (S14
            `A5`; `A1`'s fix, unchanged and LOCKED).** The container is a
            `<span className="wr-stack">` inside the flyout's `role="dialog"`,
            and neither overrides its subtree: a dialog's own name labels the
            dialog, it does not replace what is inside it. So nothing above this
            span carries the nickname, and the `role="group"` + `aria-label`
            pair is the only thing that keeps *"the full nickname is never
            lost"* true when the visible text ellipsises.
          */}
          <ChromeNickname nickname={profile.nickname} base="display-md" nameExposure="self" />
          {profile.isGuest ? <RoleChip label={t("role.guest")} /> : null}
        </span>
      </div>

      {/*
        The player's own balance, from the same `PlayerProfile` the top-bar chip
        reads (T-217, Quinn B1). It was a `null` stub from S2 until then, so the
        plate read `—` while the chip above it read the real number for the same
        identity — which breaks Ivy §9.2's LOCKED `—` ("no balance source") vs
        `0` ("zero") distinction in the one place both are on screen at once.
        `null` still renders `—` and still means what it says: no source.
      */}
      <CoinPlate value={balanceCoins} label={t("flyout.balance")} />

      <div className="wr-flyout__rows" style={{ marginTop: 16 }}>
        {/*
          **Re-pointed at S6-2b (T-260) — Ivy's own found defect, profile spec
          §2.1, and §15 rule 10: the rows are re-pointed, not removed.**

          Both rows used to `go("/welcome")`, and `/welcome` is guarded by
          `RequireStage stage="needs-identity"`. A player who has confirmed their
          identity is `ready`, so the guard bounced them straight to `/lobby`:
          two controls that looked like controls and behaved like a cancel.

          They now declare an intent to the destination — `?edit=nickname` /
          `?edit=avatar`, consumed on mount and `replaceState`d away (§2.3) —
          rather than naming a mode. `/welcome` stays a gate (§2.2) and nothing
          here re-enters it.

          **Quinn: this is a fix, not a regression.** Verify the rows land on
          `/profile` with the editor open on the right control.
        */}
        {profile.canChangeIdentity ? (
          <>
            <FlyoutRow
              label={t("flyout.changeNickname")}
              testId="flyout-change-nickname"
              disabled={offline}
              onSelect={() => go("/profile?edit=nickname")}
            />
            <FlyoutRow
              label={t("flyout.changeAvatar")}
              testId="flyout-change-avatar"
              disabled={offline}
              onSelect={() => go("/profile?edit=avatar")}
            />
          </>
        ) : null}

        <FlyoutRow
          label={t("flyout.viewProfile")}
          testId="flyout-view-profile"
          disabled={offline}
          onSelect={() => go("/profile")}
        />

        <button
          type="button"
          className="wr-flyout__row wr-label-md"
          style={{ color: "var(--wr-ink-70)" }}
          data-testid="flyout-logout"
          onClick={onLogout}
        >
          {t("nav.logout")}
        </button>
      </div>

      {/* The guest replacement for the two identity rows — not a greyed row. */}
      {profile.isGuest ? (
        <div className="wr-convert-card wr-convert-card--sm" style={{ marginTop: 16 }}>
          <p className="wr-body-sm">{t("flyout.guest.convert")}</p>
          {/*
            §14.1 r5, LOCKED: while a seat is held the card keeps its invitation
            and loses its button, replaced by the reason. Converting mid-room
            would revoke the session and manufacture a seat loss, which §13.2
            says is a different failure with a different remedy.
          */}
          {seatHeld ? (
            <p className="wr-mono-sm wr-ink-70" data-testid="flyout-convert-blocked">
              {t("convert.blocked.in_game")}
            </p>
          ) : (
            <Button
              variant="primary"
              size="sm"
              data-testid="flyout-convert"
              onClick={openConversion}
            >
              {t("flyout.guest.convertCta")}
            </Button>
          )}
        </div>
      ) : null}
    </ProfileFlyout>
  );
}

function FlyoutRow({
  label,
  onSelect,
  disabled,
  testId,
}: {
  label: string;
  onSelect: () => void;
  disabled: boolean;
  testId: string;
}) {
  return (
    <button
      type="button"
      className={`wr-flyout__row wr-label-md${disabled ? " wr-flyout__row--disabled" : ""}`}
      data-testid={testId}
      aria-disabled={disabled || undefined}
      onClick={disabled ? undefined : onSelect}
    >
      <span className="wr-stack" style={{ gap: 2 }}>
        {label}
        {disabled ? <span className="wr-mono-sm">{t("flyout.offline")}</span> : null}
      </span>
      <ChevronIcon size={18} />
    </button>
  );
}
